import React, { useRef } from 'react';
import { View } from 'react-native';
import MyAppText from './MyAppText';
import { TextInputMask, TextInputMaskMethods } from 'react-native-masked-text';
import { translate } from '../services/translate';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import moment from 'moment';

interface MyDateInputProps {
    label: string;
    date: string;
    onChangeDate: (value: string) => void;
    editable?: boolean;
    hasMaxWidth?: boolean;
    labelFontSize?: number;
    isInvalid: boolean;
    setIsInvalid: (value: boolean) => void;
    required?: boolean;
    allowDateBiggerThanToday?: boolean;
}

type InputMask = TextInputMask & TextInputMaskMethods;

export default function MyDateInput({
    label,
    date,
    onChangeDate,
    hasMaxWidth = false,
    editable = true,
    labelFontSize,
    required = false,
    allowDateBiggerThanToday = true,
    isInvalid,
    setIsInvalid,
}: MyDateInputProps) {
    const { styles, theme } = useStyles(styleSheet);

    const dateInputRef = useRef<InputMask>(null);

    return (
        <View style={{ rowGap: 5 }}>
            <View style={{ flexDirection: 'row', gap: 3, alignContent: 'center', alignItems: 'flex-start' }}>
                <MyAppText style={styles.label(labelFontSize || 14)}>{label}</MyAppText>
                {required ?
                    <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                    : null}
            </View>
            <TextInputMask
                value={date}
                placeholder='__/__/____'
                onChangeText={(text: string) => {
                    onChangeDate(text);
                }}
                keyboardType={'numeric'}
                type={'datetime'}
                style={[styles.input, hasMaxWidth ? { maxWidth: 125 } : { flex: 1 }, {
                    color: date == '' ? theme.colors.placeholder : theme.colors.textColor,
                }, !editable ? styles.disabled : undefined, isInvalid ? styles.invalid : undefined]}
                options={{
                    format: 'DD/MM/YYYY'
                }}
                onBlur={() => {
                    if (!date && !required) {
                        return setIsInvalid(false);
                    }

                    const formattedDate = moment(date, 'DD/MM/YYYY', true);

                    if (!formattedDate.isValid()) {
                        return setIsInvalid(true);
                    }

                    if (!allowDateBiggerThanToday && formattedDate.isAfter(moment())) {
                        return setIsInvalid(true);
                    }

                    return setIsInvalid(false);
                }}
                ref={dateInputRef}
                editable={editable}
            />
            {isInvalid ?
                <MyAppText style={styles.error}>{translate('invalidDate')}</MyAppText> : null}
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    label: (fontSize: number) => ({
        color: theme.colors.labelColor,
        fontSize,
    }),
    input: {
        fontSize: 16,
        fontFamily: 'Open Sans',
        backgroundColor: theme.colors.field.background,
        borderColor: theme.colors.field.border,
        borderWidth: 1,
        maxHeight: 40,
        padding: 10,
        borderRadius: 4,
        color: theme.colors.field.text,
    },
    error: {
        color: '#FF0000',
        fontSize: 10
    },
    invalid: {
        borderColor: '#FF0000',
    },
    disabled: {
        backgroundColor: theme.colors.field.disabledBackground,
    }
}));
